import { useTranslation } from "@vixi/localization";
import { ControlledInputProps, OmitControlledInputProps } from "@vixi/types";
import { ComponentProps, forwardRef, useImperativeHandle } from "react";
import { useFileInput } from "@/hooks/useFileInput";
import { buttonVariants } from "@/components/base/button";
import { Chip } from "@/components/base/chip";

interface AttachButtonProps
  extends ControlledInputProps<File[]>,
    OmitControlledInputProps<React.InputHTMLAttributes<HTMLInputElement>> {
  multiple?: boolean;
  slotProps?: {
    button?: Partial<ComponentProps<"label">>;
  };
}

export const AttachButton = forwardRef<HTMLInputElement, AttachButtonProps>(
  (
    { multiple = false, slotProps, value: files, onChange, onBlur, children, ...inputProps },
    ref,
  ) => {
    const { t } = useTranslation();
    const { inputRef, handleFileChange, handleRemoveFile } = useFileInput({
      multiple,
      value: files,
      onChange,
      onBlur,
    });

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    return (
      <>
        <div>
          <label
            {...slotProps?.button}
            className={buttonVariants({
              variant: "outlined",
              size: "pill",
            })}
          >
            {children || t("Action.AttachPicture")}
            <input
              {...inputProps}
              ref={inputRef}
              type="file"
              hidden
              onChange={handleFileChange}
              multiple={multiple}
            />
          </label>
        </div>

        {files && (
          <div className="mt-1 flex flex-wrap items-start gap-1.5">
            {files.map((file, i) => (
              <Chip
                key={`file.name-${i}`}
                label={file.name}
                onClick={() => {
                  if (file instanceof File) {
                    const url = URL.createObjectURL(file);
                    window.open(url, "_blank");
                  }
                }}
                onDelete={() => handleRemoveFile(i)}
              />
            ))}
          </div>
        )}
      </>
    );
  },
);
